import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getToken, setToken as saveToken, clearToken } from './hiddenAuth';
import { API_BASE } from './apiBase';

// Publishing service on the Mac (Cloudflare Tunnel -> 127.0.0.1:8800).
// Override for local dev with REACT_APP_PUBLISH_BASE=http://localhost:8800
const PUBLISH_BASE = process.env.REACT_APP_PUBLISH_BASE || 'https://publish.fabianmorelli.de';

const BUILTIN_ORDER = ['gallery', '1x1', '13x7'];
// built-ins first, then anything created through this page
const orderOf = (cats) => [
  ...BUILTIN_ORDER.filter((c) => cats && cats[c]),
  ...Object.keys(cats || {}).filter((c) => !BUILTIN_ORDER.includes(c)),
];

// Pixel size for a ratio scaled so its longer side is `longSide` — mirrors the
// server, purely so the form can preview what you'll get.
function newCatSize(longSide, aw, ah) {
  const L = Number(longSide) || 0, w = Number(aw) || 0, h = Number(ah) || 0;
  if (!L || !w || !h) return '—';
  return w >= h ? `${L}×${Math.max(1, Math.round(L * h / w))}`
                : `${Math.max(1, Math.round(L * w / h))}×${L}`;
}

// Largest crop of the required aspect that fits the image, centred.
// Crop rects are normalized (0..1) against the image's own width/height.
function defaultCrop(imgW, imgH, aspect) {
  let w, h;
  if (imgW / imgH >= aspect) {          // image wider than the target -> height limits
    h = 1;
    w = (aspect * imgH) / imgW;
  } else {
    w = 1;
    h = imgW / (aspect * imgH);
  }
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

// Resize a crop about its centre, keeping the aspect and staying in bounds.
function scaleCrop(crop, scale, imgW, imgH, aspect) {
  const max = defaultCrop(imgW, imgH, aspect);
  const w = Math.min(max.w, Math.max(max.w * 0.25, max.w * scale));
  const h = (w * imgW) / (aspect * imgH);
  const cx = crop.x + crop.w / 2;
  const cy = crop.y + crop.h / 2;
  return {
    w, h,
    x: Math.min(1 - w, Math.max(0, cx - w / 2)),
    y: Math.min(1 - h, Math.max(0, cy - h / 2)),
  };
}

function CropBox({ src, crop, onChange }) {
  const wrapRef = useRef(null);
  const drag = useRef(null);

  const onPointerDown = (e) => {
    const box = wrapRef.current.getBoundingClientRect();
    drag.current = { px: e.clientX, py: e.clientY, x: crop.x, y: crop.y, box };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.px) / d.box.width;
    const dy = (e.clientY - d.py) / d.box.height;
    onChange({
      ...crop,
      x: Math.min(1 - crop.w, Math.max(0, d.x + dx)),
      y: Math.min(1 - crop.h, Math.max(0, d.y + dy)),
    });
  };
  const onPointerUp = () => { drag.current = null; };

  return (
    <div className="pub-cropwrap" ref={wrapRef}>
      <img src={src} alt="" draggable={false} />
      <div
        className="pub-cropbox"
        style={{
          left: `${crop.x * 100}%`, top: `${crop.y * 100}%`,
          width: `${crop.w * 100}%`, height: `${crop.h * 100}%`,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  );
}

export default function Publish() {
  const [token, setTok] = useState(getToken);
  const [phase, setPhase] = useState(() => (getToken() ? 'unlocked' : 'locked'));
  const [pass, setPass] = useState('');
  const [lockError, setLockError] = useState('');

  const [cats, setCats] = useState(null);      // from /health
  const [offline, setOffline] = useState(false);

  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [dims, setDims] = useState(null);      // natural size of the chosen file
  const [sel, setSel] = useState({});          // category -> {position, crop, scale}
  const [active, setActive] = useState(null);  // which crop tab is showing
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // --- manage existing photos ---
  const [mode, setMode] = useState('upload');      // 'upload' | 'manage'
  const [mCat, setMCat] = useState('gallery');
  const [mEntries, setMEntries] = useState([]);
  const [mSel, setMSel] = useState(null);          // selected position
  const [mSrc, setMSrc] = useState(null);          // {url, w, h} of the selection
  const [mTargets, setMTargets] = useState({});
  const [mActive, setMActive] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [reorderTo, setReorderTo] = useState(0);
  const [moveOut, setMoveOut] = useState(false);   // copy, or move out of the source?
  const [movedOutOf, setMovedOutOf] = useState(null);
  const [mStatus, setMStatus] = useState({ state: 'idle', msg: '' });
  const [newCat, setNewCat] = useState({ open: false, label: '', aspect_w: 13, aspect_h: 7, image_px: 1857, thumb_px: 0 });
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [confirmDelCat, setConfirmDelCat] = useState(false);

  const probe = useCallback(async (tok) => {
    try {
      const r = await fetch(`${PUBLISH_BASE}/health`, { headers: { Authorization: `Bearer ${tok}` } });
      if (r.status === 401) return 'unauthorized';
      if (!r.ok) return 'offline';
      const d = await r.json();
      setCats(d.categories || {});
      return 'online';
    } catch (e) { return 'offline'; }
  }, []);

  useEffect(() => {
    if (phase !== 'unlocked' || !token) return;
    let on = true;
    probe(token).then((res) => {
      if (!on) return;
      if (res === 'unauthorized') { clearToken(); setTok(''); setPhase('locked'); setLockError('Saved password was rejected.'); }
      else setOffline(res === 'offline');
    });
    return () => { on = false; };
  }, [phase, token, probe]);

  async function unlock(e) {
    e.preventDefault();
    const t = pass.trim();
    if (!t) return;
    const res = await probe(t);
    if (res === 'unauthorized') { setLockError('Wrong password.'); return; }
    if (res === 'offline') { setLockError("Can't reach your Mac — is it on with the tunnel running?"); return; }
    saveToken(t); setTok(t); setPass(''); setPhase('unlocked');
  }

  function pickFile(f) {
    if (!f) return;
    setResult(null); setError('');
    setFile(f);
    const u = URL.createObjectURL(f);
    setUrl(u);
    const im = new Image();
    im.onload = () => {
      setDims({ w: im.naturalWidth, h: im.naturalHeight });
      // seed a centred crop for every category
      const seeded = {};
      orderOf(cats).forEach((c) => {
        if (!cats || !cats[c]) return;
        seeded[c] = {
          on: false, position: 0, scale: 1,
          crop: defaultCrop(im.naturalWidth, im.naturalHeight, cats[c].crop_aspect),
        };
      });
      setSel(seeded);
      setActive(null);
    };
    im.src = u;
  }

  const toggle = (c) => setSel((s) => {
    const next = { ...s, [c]: { ...s[c], on: !s[c].on } };
    if (next[c].on && !active) setActive(c);
    return next;
  });

  const setPos = (c, v) => setSel((s) => ({ ...s, [c]: { ...s[c], position: v } }));

  const setScale = (c, v) => setSel((s) => ({
    ...s, [c]: { ...s[c], scale: v, crop: scaleCrop(s[c].crop, v, dims.w, dims.h, cats[c].crop_aspect) },
  }));

  const setCrop = (c, crop) => setSel((s) => ({ ...s, [c]: { ...s[c], crop } }));

  const chosen = orderOf(cats).filter((c) => sel[c] && sel[c].on);

  // Use the in-session token, not localStorage: a browser with site storage
  // blocked still has a valid token here, and re-reading would come back empty.
  const loadGallery = useCallback(async (cat) => {
    const tok = token || getToken();
    if (!tok) { setMStatus({ state: 'error', msg: 'No password stored — lock and unlock again.' }); return; }
    setMStatus({ state: 'loading', msg: '' });
    try {
      const r = await fetch(`${PUBLISH_BASE}/list/${cat}`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) {
        setMEntries([]);
        setMStatus({ state: 'error', msg: r.status === 401
          ? 'Password rejected — lock and unlock again.'
          : `Couldn't load the gallery (HTTP ${r.status}).` });
        return;
      }
      const d = await r.json();
      setMEntries(d.entries || []);
      setMStatus({ state: 'ready', msg: '' });
      setMSel(null); setMSrc(null); setMTargets({}); setMActive(null); setConfirmDel(false);
      setConfirmDelCat(false);
    } catch (e) {
      setMEntries([]);
      setMStatus({ state: 'error', msg: "Couldn't reach your Mac — is it on with the tunnel running?" });
    }
  }, [token]);

  useEffect(() => {
    if (phase === 'unlocked' && mode === 'manage') loadGallery(mCat);
  }, [phase, mode, mCat, loadGallery]);

  // Selecting a photo seeds a centred crop for every other gallery.
  function selectPhoto(entry) {
    setMSel(entry.position); setConfirmDel(false); setError('');
    setReorderTo(entry.position); setMoveOut(false);
    const url = `${API_BASE}${entry.url}`;
    const im = new Image();
    im.onload = () => {
      setMSrc({ url, w: im.naturalWidth, h: im.naturalHeight });
      const seeded = {};
      orderOf(cats).forEach((c) => {
        if (!cats || !cats[c] || c === mCat) return;
        seeded[c] = { on: false, position: 0, scale: 1,
                      crop: defaultCrop(im.naturalWidth, im.naturalHeight, cats[c].crop_aspect) };
      });
      setMTargets(seeded); setMActive(null);
    };
    im.src = url;
  }

  const mChosen = orderOf(cats).filter((c) => mTargets[c] && mTargets[c].on);

  async function deleteCategory() {
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await fetch(`${PUBLISH_BASE}/category/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id: mCat }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setError(d.detail || `Could not delete it (HTTP ${r.status})`);
      else {
        setResult([{ ...d.deleted, deletedGallery: true }]);
        setConfirmDelCat(false); setMEntries([]); setMSel(null);
        await probe(token);
        setMCat('gallery');
      }
    } catch (e) { setError('Could not reach the Mac.'); }
    finally { setBusy(false); }
  }

  async function createCategory() {
    const label = newCat.label.trim();
    if (!label) { setError('Give the gallery a name.'); return; }
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await fetch(`${PUBLISH_BASE}/category`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label,
                               aspect_w: Number(newCat.aspect_w) || 0,
                               aspect_h: Number(newCat.aspect_h) || 0,
                               image_px: Number(newCat.image_px) || 1400,
                               thumb_px: Number(newCat.thumb_px) || 0 }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setError(d.detail || `Could not create it (HTTP ${r.status})`);
      else {
        setNewCat({ open: false, label: '', aspect_w: 13, aspect_h: 7, image_px: 1857, thumb_px: 0 });
        await probe(token);
        setMCat(d.gallery.id);
      }
    } catch (e) { setError('Could not reach the Mac.'); }
    finally { setBusy(false); }
  }

  // Drag a tile onto another to drop it at that position.
  async function dropAt(to) {
    const from = dragFrom;
    setDragFrom(null); setDragOver(null);
    if (from === null || to === null || from === to) return;
    setBusy(true); setError('');
    // reflect the new order straight away, then confirm with the server
    setMEntries((list) => {
      const next = list.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((e, i) => ({ ...e, position: i }));
    });
    try {
      const r = await fetch(`${PUBLISH_BASE}/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category: mCat, position: from, to }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.detail || 'Reorder failed.'); }
    } catch (e) { setError('Could not reach the Mac.'); }
    finally { setBusy(false); loadGallery(mCat); }
  }

  async function doReorder() {
    if (mSel === null) return;
    setBusy(true); setError(''); setResult(null); setMovedOutOf(null);
    try {
      const r = await fetch(`${PUBLISH_BASE}/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category: mCat, position: mSel, to: Number(reorderTo) || 0 }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setError(d.detail || `Move failed (HTTP ${r.status})`);
      else { setResult([{ ...d.moved, reordered: true }]); loadGallery(mCat); }
    } catch (e) { setError('Could not reach the Mac.'); }
    finally { setBusy(false); }
  }

  async function doCopy() {
    if (mSel === null || mChosen.length === 0) return;
    setBusy(true); setError(''); setResult(null); setMovedOutOf(null);
    try {
      const r = await fetch(`${PUBLISH_BASE}/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_category: mCat, source_position: mSel, move: moveOut,
          targets: mChosen.map((c) => ({ category: c, position: Number(mTargets[c].position) || 0, crop: mTargets[c].crop })),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setError(d.detail || `Copy failed (HTTP ${r.status})`);
      else { setResult(d.published || []); setMovedOutOf(d.removed_from_source || null); probe(token); loadGallery(mCat); }
    } catch (e) { setError('Could not reach the Mac.'); }
    finally { setBusy(false); }
  }

  async function doRemove() {
    if (mSel === null) return;
    setBusy(true); setError(''); setResult(null); setMovedOutOf(null);
    try {
      const r = await fetch(`${PUBLISH_BASE}/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category: mCat, position: mSel }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setError(d.detail || `Remove failed (HTTP ${r.status})`);
      else { setResult([{ ...d.removed, removed: true }]); probe(token); loadGallery(mCat); }
    } catch (e) { setError('Could not reach the Mac.'); }
    finally { setBusy(false); }
  }

  async function submit() {
    if (!file || chosen.length === 0) return;
    setBusy(true); setError(''); setResult(null); setMovedOutOf(null);
    const targets = chosen.map((c) => ({
      category: c,
      position: Number(sel[c].position) || 0,
      crop: sel[c].crop,
    }));
    const body = new FormData();
    body.append('file', file);
    body.append('targets', JSON.stringify(targets));
    try {
      const r = await fetch(`${PUBLISH_BASE}/publish`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.detail || `Upload failed (HTTP ${r.status})`); }
      else {
        setResult(d.published || []);
        setFile(null); setUrl(''); setDims(null); setSel({}); setActive(null);
        probe(token);   // refresh the counts
      }
    } catch (e) {
      setError('Could not reach the Mac.');
    } finally { setBusy(false); }
  }

  if (phase === 'locked') {
    return (
      <div className="lab-lock">
        <form className="lab-lock-card" onSubmit={unlock}>
          <h1>Publish</h1>
          <p className="lab-sub">Enter the password to add a photo to the galleries.</p>
          <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Password" autoFocus />
          <button type="submit">Unlock</button>
          {lockError && <p className="lab-error">{lockError}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="page pub">
      <div className="pub-head">
        <h1 className="page-title">Publish a photo</h1>
        <button className="lab-lockbtn" onClick={() => { clearToken(); setTok(''); setPhase('locked'); }}>Lock</button>
      </div>

      {offline && <div className="lab-banner">Can't reach your Mac — is it on with the tunnel running?</div>}

      <div className="pub-modes">
        <button className={`pub-mode${mode === 'upload' ? ' on' : ''}`} onClick={() => { setMode('upload'); setResult(null); setError(''); }}>Upload new</button>
        <button className={`pub-mode${mode === 'manage' ? ' on' : ''}`} onClick={() => { setMode('manage'); setResult(null); setError(''); }}>Manage existing</button>
      </div>

      {mode === 'upload' && <label className="pub-drop">
        <input type="file" accept="image/*,.heic,.heif" onChange={(e) => pickFile(e.target.files[0])} />
        {file ? <span>{file.name} — {(file.size / 1048576).toFixed(1)} MB{dims ? ` · ${dims.w}×${dims.h}` : ''}</span>
              : <span>Choose a photo…</span>}
      </label>}

      {mode === 'upload' && file && cats && (
        <>
          <div className="pub-cats">
            {orderOf(cats).map((c) => (
              <div key={c} className={`pub-cat${sel[c] && sel[c].on ? ' on' : ''}`}>
                <label className="pub-cat-head">
                  <input type="checkbox" checked={!!(sel[c] && sel[c].on)} onChange={() => toggle(c)} />
                  <span className="pub-cat-name">{cats[c].label}</span>
                  <span className="pub-cat-meta">{cats[c].count} photos</span>
                </label>
                {sel[c] && sel[c].on && (
                  <div className="pub-cat-body">
                    <label className="pub-field">
                      <span>Position</span>
                      <input type="number" min="0" max={cats[c].count} value={sel[c].position}
                             onChange={(e) => setPos(c, e.target.value)} />
                      <em>0 = first</em>
                    </label>
                    <button className={`pub-croptab${active === c ? ' on' : ''}`} onClick={() => setActive(c)}>
                      {active === c ? 'Editing crop' : 'Edit crop'}
                    </button>
                    <p className="pub-note">
                      {cats[c].crops_image
                        ? `image ${cats[c].image_size[0]}×${cats[c].image_size[1]} from this crop`
                          + (cats[c].thumb_size ? ` · thumb ${cats[c].thumb_size[0]}×${cats[c].thumb_size[1]}` : ' · no thumbnail needed')
                        : `image ${cats[c].image_size}px long side (uncropped)`
                          + (cats[c].thumb_size ? ` · thumb ${cats[c].thumb_size[0]}×${cats[c].thumb_size[1]} from this crop` : '')}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {active && sel[active] && sel[active].on && dims && (
            <div className="pub-cropper">
              <div className="pub-cropper-head">
                <strong>{cats[active].label}</strong> — drag the box to choose the crop
              </div>
              <CropBox src={url} crop={sel[active].crop} onChange={(cr) => setCrop(active, cr)} />
              <label className="pub-zoom">
                <span>size</span>
                <input type="range" min="0.25" max="1" step="0.01"
                       value={sel[active].scale} onChange={(e) => setScale(active, parseFloat(e.target.value))} />
              </label>
            </div>
          )}

          <div className="pub-actions">
            <button className="pub-go" disabled={busy || chosen.length === 0} onClick={submit}>
              {busy ? 'Publishing…' : `Publish to ${chosen.length || 'no'} galler${chosen.length === 1 ? 'y' : 'ies'}`}
            </button>
            {error && <span className="lab-error">{error}</span>}
          </div>
        </>
      )}

      {mode === 'manage' && cats && (
        <div className="pub-manage">
          <div className="pub-galtabs">
            {orderOf(cats).map((c) => (
              <button key={c} className={`pub-galtab${mCat === c ? ' on' : ''}`} onClick={() => setMCat(c)}>
                {cats[c].label} <span className="pub-cat-meta">{cats[c].count}</span>
              </button>
            ))}
            <button className="pub-galtab pub-newcat" onClick={() => setNewCat((n) => ({ ...n, open: !n.open }))}>
              + New gallery
            </button>
          </div>

          {newCat.open && (
            <div className="pub-newcat-form">
              <label className="pub-field">
                <span>Name</span>
                <input type="text" value={newCat.label} placeholder="e.g. Portraits"
                       onChange={(e) => setNewCat((n) => ({ ...n, label: e.target.value }))} />
              </label>
              <label className="pub-field">
                <span>Shape</span>
                <input type="number" min="1" max="100" value={newCat.aspect_w}
                       onChange={(e) => setNewCat((n) => ({ ...n, aspect_w: e.target.value }))} />
                <em>:</em>
                <input type="number" min="1" max="100" value={newCat.aspect_h}
                       onChange={(e) => setNewCat((n) => ({ ...n, aspect_h: e.target.value }))} />
                <em>every photo is cropped to this</em>
              </label>
              <label className="pub-field">
                <span>Image long side</span>
                <input type="number" min="64" max="6000" value={newCat.image_px}
                       onChange={(e) => setNewCat((n) => ({ ...n, image_px: e.target.value }))} />
                <em>px → {newCatSize(newCat.image_px, newCat.aspect_w, newCat.aspect_h)}</em>
              </label>
              <label className="pub-field">
                <span>Thumbnail long side</span>
                <input type="number" min="0" max="6000" value={newCat.thumb_px}
                       onChange={(e) => setNewCat((n) => ({ ...n, thumb_px: e.target.value }))} />
                <em>{Number(newCat.thumb_px) > 0
                      ? `px → ${newCatSize(newCat.thumb_px, newCat.aspect_w, newCat.aspect_h)}`
                      : 'px — 0 for none, like 13×7'}</em>
              </label>
              <p className="pub-note">
                Thumbnails use the same shape as the images. Uploads are cropped to it and
                scaled automatically.
              </p>
              <div className="pub-actions">
                <button className="pub-go" disabled={busy || !newCat.label.trim()} onClick={createCategory}>
                  {busy ? 'Creating…' : 'Create gallery'}
                </button>
                <button onClick={() => setNewCat((n) => ({ ...n, open: false }))}>Cancel</button>
              </div>
            </div>
          )}

          {cats[mCat] && cats[mCat].custom && (
            <div className="pub-actions pub-delcat">
              {confirmDelCat ? (
                <>
                  <span>Delete <strong>{cats[mCat].label}</strong> and its {cats[mCat].count} photo{cats[mCat].count === 1 ? '' : 's'}?</span>
                  <button className="pub-danger" disabled={busy} onClick={deleteCategory}>Yes, delete</button>
                  <button onClick={() => setConfirmDelCat(false)}>Cancel</button>
                </>
              ) : (
                <button className="pub-danger" onClick={() => setConfirmDelCat(true)}>Delete this gallery</button>
              )}
            </div>
          )}

          {mStatus.state === 'ready' && mEntries.length > 1 && (
            <p className="pub-note">Drag a photo onto another to move it there.</p>
          )}
          {mStatus.state === 'loading' && <p className="pub-note">Loading photos…</p>}
          {mStatus.state === 'error' && <div className="lab-banner">{mStatus.msg}</div>}
          {mStatus.state === 'ready' && mEntries.length === 0 && (
            <p className="pub-note">This gallery has no photos yet.</p>
          )}

          <div className="pub-grid">
            {mEntries.map((e) => (
              <button key={e.filename + e.position}
                      className={`pub-tile${mSel === e.position ? ' on' : ''}`
                        + (dragOver === e.position && dragFrom !== e.position ? ' dragover' : '')
                        + (dragFrom === e.position ? ' dragging' : '')}
                      title={`${e.filename} — position ${e.position} (drag to reorder)`}
                      draggable
                      onDragStart={() => setDragFrom(e.position)}
                      onDragOver={(ev) => { ev.preventDefault(); setDragOver(e.position); }}
                      onDragLeave={() => setDragOver((d) => (d === e.position ? null : d))}
                      onDrop={(ev) => { ev.preventDefault(); dropAt(e.position); }}
                      onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                      onClick={() => selectPhoto(e)}>
                {/* small server-rendered preview; `loading="lazy"` is deliberately
                    absent — inside this scroll container the browser never
                    requested the images and every tile stayed blank */}
                <img src={`${API_BASE}${e.preview_url || e.url}`} alt={`#${e.position}`} decoding="async"
                     onError={(ev) => { ev.currentTarget.classList.add('pub-tile-broken'); }} />
                <span className="pub-tile-pos">{e.position}</span>
              </button>
            ))}
          </div>

          {mSel !== null && (
            <div className="pub-selected">
              <p className="pub-note">
                Selected position <strong>{mSel}</strong> in {cats[mCat].label}
                {mSrc ? ` · source ${mSrc.w}×${mSrc.h}` : ''}
              </p>

              <label className="pub-field">
                <span>Move to position</span>
                <input type="number" min="0" max={Math.max(0, cats[mCat].count - 1)}
                       value={reorderTo} onChange={(e) => setReorderTo(e.target.value)} />
                <button disabled={busy || Number(reorderTo) === mSel} onClick={doReorder}>Move</button>
                <em>reorders within {cats[mCat].label}; nothing is re-encoded</em>
              </label>

              <div className="pub-actions">
                {confirmDel ? (
                  <>
                    <span>Remove this photo from {cats[mCat].label}?</span>
                    <button className="pub-danger" disabled={busy} onClick={doRemove}>Yes, remove</button>
                    <button onClick={() => setConfirmDel(false)}>Cancel</button>
                  </>
                ) : (
                  <button className="pub-danger" onClick={() => setConfirmDel(true)}>Remove from this gallery</button>
                )}
              </div>

              <h3 className="pub-subhead">{moveOut ? 'Move it to' : 'Also add it to'}</h3>
              <label className="pub-moveout">
                <input type="checkbox" checked={moveOut} onChange={() => setMoveOut((v) => !v)} />
                <span>Move instead of copy — remove it from {cats[mCat].label} afterwards</span>
              </label>
              <div className="pub-cats">
                {orderOf(cats).filter((c) => c !== mCat).map((c) => (
                  <div key={c} className={`pub-cat${mTargets[c] && mTargets[c].on ? ' on' : ''}`}>
                    <label className="pub-cat-head">
                      <input type="checkbox" checked={!!(mTargets[c] && mTargets[c].on)}
                             onChange={() => setMTargets((t) => ({ ...t, [c]: { ...t[c], on: !t[c].on } }))} />
                      <span className="pub-cat-name">{cats[c].label}</span>
                      <span className="pub-cat-meta">{cats[c].count} photos</span>
                    </label>
                    {mTargets[c] && mTargets[c].on && (
                      <div className="pub-cat-body">
                        <label className="pub-field">
                          <span>Position</span>
                          <input type="number" min="0" max={cats[c].count} value={mTargets[c].position}
                                 onChange={(ev) => setMTargets((t) => ({ ...t, [c]: { ...t[c], position: ev.target.value } }))} />
                          <em>0 = first</em>
                        </label>
                        <button className={`pub-croptab${mActive === c ? ' on' : ''}`} onClick={() => setMActive(c)}>
                          {mActive === c ? 'Editing crop' : 'Edit crop'}
                        </button>
                        {mSrc && cats[c].image_size && mSrc.w < (Array.isArray(cats[c].image_size) ? cats[c].image_size[0] : cats[c].image_size) && (
                          <p className="pub-warn">Source is smaller than this gallery's size — it will be upscaled. Publishing the original gives better quality.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {mActive && mTargets[mActive] && mTargets[mActive].on && mSrc && (
                <div className="pub-cropper">
                  <div className="pub-cropper-head"><strong>{cats[mActive].label}</strong> — drag the box to choose the crop</div>
                  <CropBox src={mSrc.url} crop={mTargets[mActive].crop}
                           onChange={(cr) => setMTargets((t) => ({ ...t, [mActive]: { ...t[mActive], crop: cr } }))} />
                  <label className="pub-zoom">
                    <span>size</span>
                    <input type="range" min="0.25" max="1" step="0.01" value={mTargets[mActive].scale}
                           onChange={(ev) => { const v = parseFloat(ev.target.value);
                             setMTargets((t) => ({ ...t, [mActive]: { ...t[mActive], scale: v,
                               crop: scaleCrop(t[mActive].crop, v, mSrc.w, mSrc.h, cats[mActive].crop_aspect) } })); }} />
                  </label>
                </div>
              )}

              <div className="pub-actions">
                <button className="pub-go" disabled={busy || mChosen.length === 0} onClick={doCopy}>
                  {busy ? 'Working…' : `${moveOut ? 'Move' : 'Add'} to ${mChosen.length || 'no'} galler${mChosen.length === 1 ? 'y' : 'ies'}`}
                </button>
                {error && <span className="lab-error">{error}</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="pub-result">
          <h2>{result[0] && result[0].deletedGallery ? 'Gallery deleted'
                : result[0] && result[0].reordered ? 'Reordered'
                : result[0] && result[0].removed ? 'Removed'
                : movedOutOf ? 'Moved' : 'Published'}</h2>
          <ul>
            {result.map((r, i) => r.deletedGallery ? (
              <li key={i}>
                Deleted <strong>{r.label}</strong> with {r.photos} photo{r.photos === 1 ? '' : 's'}.
                {r.trash && <> Moved to <code>{r.trash}</code> on the Mac, so it can still be recovered.</>}
              </li>
            ) : r.reordered ? (
              <li key={i}>
                <strong>{cats && cats[r.category] ? cats[r.category].label : r.category}</strong>:
                {' '}moved position {r.position} to {r.to} ({r.total} photos)
              </li>
            ) : r.removed ? (
              <li key={i}>
                <strong>{cats && cats[r.category] ? cats[r.category].label : r.category}</strong>:
                {' '}removed position {r.position} ({r.total} photos left)
              </li>
            ) : (
              <li key={i}>
                <strong>{cats && cats[r.category] ? cats[r.category].label : r.category}</strong>:
                {' '}{r.filename} at position {r.position} — {r.image_px[0]}×{r.image_px[1]} image
                {r.thumb_px ? `, ${r.thumb_px[0]}×${r.thumb_px[1]} thumbnail` : ''} ({r.total} photos now)
              </li>
            ))}
          </ul>
          {movedOutOf && (
            <p className="pub-note">Removed from {cats && cats[movedOutOf.category] ? cats[movedOutOf.category].label : movedOutOf.category}
              {' '}(position {movedOutOf.position}, {movedOutOf.total} photos left).</p>
          )}
          <p className="pub-note">Live on the site immediately — the galleries are served from the Mac.</p>
        </div>
      )}
    </div>
  );
}
